import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Menu, X } from "lucide-react";
import { CATEGORIES } from "../lib/categories";
import { useCart } from "../context/CartContext";

export default function Header() {
  const navigate = useNavigate();
  const { itemCount } = useCart();
  const [query, setQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMenuOpen(false);
  };

  return (
    <header
      className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-neutral-200"
      data-testid="site-header"
    >
      {/* Top row */}
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <div className="flex items-center gap-4 py-4">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2 shrink-0"
          >
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-neutral-900 text-white font-display font-bold text-lg tracking-tight">
              B
            </span>
            <span className="font-display font-bold text-xl tracking-tight text-neutral-900">
              Broke<span className="brand-red">Shopping</span>
              <span className="text-neutral-400 font-medium">.com</span>
            </span>
          </Link>

          {/* Search - desktop */}
          <form
            onSubmit={submitSearch}
            className="hidden md:flex flex-1 max-w-2xl relative"
            data-testid="search-form"
          >
            <Search
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500"
              size={18}
              strokeWidth={1.5}
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search cars, hotels, snorkel sets, everything…"
              className="w-full h-11 pl-11 pr-24 rounded-full border border-neutral-300 bg-white focus:outline-none focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10 text-sm placeholder-neutral-400 transition-colors duration-200"
              data-testid="search-input"
            />
            <button
              type="submit"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 px-4 rounded-full bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 transition-colors duration-200"
              data-testid="search-submit-button"
            >
              Search
            </button>
          </form>

          <div className="flex items-center gap-1 ml-auto">
            {/* Food delivery link */}
            <Link
              to="/food"
              className="hidden md:inline-flex items-center gap-2 px-3 h-11 rounded-full hover:bg-neutral-100 transition-colors duration-200"
              data-testid="nav-food-link"
            >
              <span className="text-sm font-medium text-neutral-900">Food</span>
              <span className="inline-flex items-center px-2 h-5 rounded-full bg-brand text-white text-[10px] font-bold uppercase tracking-wide">
                New
              </span>
            </Link>

            {/* Cart */}
            <Link
              to="/cart"
              className="relative inline-flex items-center gap-2 px-3 h-11 rounded-full hover:bg-neutral-100 transition-colors duration-200"
              data-testid="cart-icon-link"
            >
              <ShoppingBag size={20} strokeWidth={1.5} className="text-neutral-900" />
              <span className="hidden sm:inline text-sm font-medium text-neutral-900">
                Cart
              </span>
              {itemCount > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1.5 rounded-full bg-brand text-white text-[11px] font-bold inline-flex items-center justify-center"
                >
                  {itemCount}
                </span>
              )}
            </Link>

            {/* Mobile menu button */}
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="md:hidden h-10 w-10 inline-flex items-center justify-center rounded-full hover:bg-neutral-100 transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile search */}
        <form
          onSubmit={submitSearch}
          className="md:hidden pb-3"
          data-testid="mobile-search-form"
        >
          <div className="relative">
            <Search
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500"
              size={18}
              strokeWidth={1.5}
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search everything…"
              className="w-full h-11 pl-11 pr-4 rounded-full border border-neutral-300 focus:outline-none focus:border-neutral-900 text-sm placeholder-neutral-400"
              data-testid="mobile-search-input"
            />
          </div>
        </form>
      </div>

      {/* Category strip - desktop (shows ALL categories) */}
      <nav
        className="hidden md:block border-t border-neutral-200/80"
        data-testid="category-nav"
      >
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <ul className="flex items-center gap-1 py-2 overflow-x-auto whitespace-nowrap">
            {CATEGORIES.map((c) => (
              <li key={c.slug} className="shrink-0">
                <Link
                  to={`/category/${c.slug}`}
                  className="inline-flex items-center px-3 h-9 rounded-full text-sm text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                  data-testid={`nav-category-${c.slug}`}
                >
                  {c.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Mobile menu drawer */}
      {menuOpen && (
        <div
          className="md:hidden border-t border-neutral-200 bg-white"
          data-testid="mobile-menu"
        >
          <ul className="px-5 py-3 grid grid-cols-2 gap-1">
            <li className="col-span-2">
              <Link
                to="/food"
                onClick={() => setMenuOpen(false)}
                className="block px-3 py-2 rounded-md bg-brand/10 text-brand text-sm font-semibold"
                data-testid="mobile-nav-food-link"
              >
                🛵 Food Delivery — New
              </Link>
            </li>
            {CATEGORIES.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/category/${c.slug}`}
                  onClick={() => setMenuOpen(false)}
                  className="block px-3 py-2 rounded-md hover:bg-neutral-100 text-sm text-neutral-800"
                  data-testid={`mobile-menu-category-${c.slug}`}
                >
                  {c.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
}
